/**
 * Defines the sections displayed on the company report page and their metadata.
 * Component imports moved to page.tsx for better HMR performance.
 */
import type { ReactNode } from "react"

import { getBuyerBewareSectionTitle } from "@/helpers/company/buyer-beware"
import type { MethodologyOutput } from "@/types/signal-types"

export enum CompanySection {
  REVIEW_HISTORY = "review_history",
  WHAT_STOOD_OUT = "what_stood_out",
  PLATFORMS_MONITORED = "platforms_monitored",
  HOW_WE_GOT_SCORE = "how_we_got_score",
  WHAT_YOU_CAN_EXPECT = "what_you_can_expect",
  PERFORMANCE_BY_WORK_TYPE = "performance_by_work_type",
  LONG_TERM_SATISFACTION = "long_term_satisfaction",
  BACKGROUND_CHECK = "background_check",
  BUYER_BEWARE = "buyer_beware",
}

/** Ordered list of sections to display on the company page */
export const ORDERED_COMPANY_SECTIONS = [
  CompanySection.WHAT_STOOD_OUT,
  CompanySection.PLATFORMS_MONITORED,
  CompanySection.PERFORMANCE_BY_WORK_TYPE,
  CompanySection.HOW_WE_GOT_SCORE,
  CompanySection.BUYER_BEWARE,
  CompanySection.BACKGROUND_CHECK,
  CompanySection.WHAT_YOU_CAN_EXPECT,
  CompanySection.LONG_TERM_SATISFACTION,
  // CompanySection.REVIEW_HISTORY,
]

type SectionData = {
  methodologyOutput: MethodologyOutput
  deceptionCounts: Record<string, number>
}

type SectionConfig = {
  getTitle: (data: SectionData) => ReactNode
  /** Short label for navigation. If set, section appears in nav. */
  navLabel?: string
}

export const dataByCompanySection: Record<CompanySection, SectionConfig> = {
  [CompanySection.REVIEW_HISTORY]: {
    getTitle: () => "Review History",
    navLabel: "REVIEWS",
  },
  [CompanySection.WHAT_STOOD_OUT]: {
    getTitle: () => "The Verdict",
    navLabel: "VERDICT",
  },
  [CompanySection.PLATFORMS_MONITORED]: {
    getTitle: () => "Platforms Monitored",
    navLabel: "PLATFORMS",
  },
  [CompanySection.HOW_WE_GOT_SCORE]: {
    navLabel: "HOW WE SCORE",
    getTitle: ({ methodologyOutput }) => (
      <>
        How We Got To Trust Score{" "}
        <span className="brand-light text-primary inline-block rounded border-2 px-2 py-1">
          {Math.round(methodologyOutput.trustScore)}
        </span>
      </>
    ),
  },
  [CompanySection.WHAT_YOU_CAN_EXPECT]: {
    getTitle: () => "What You Can Expect",
    navLabel: "WHAT TO EXPECT",
  },
  [CompanySection.PERFORMANCE_BY_WORK_TYPE]: {
    getTitle: () => "Performance by Work Type",
    navLabel: "WORK TYPES",
  },
  [CompanySection.LONG_TERM_SATISFACTION]: {
    navLabel: "SATISFACTION",
    getTitle: () => "Long-term Satisfaction",
  },
  [CompanySection.BACKGROUND_CHECK]: {
    navLabel: "BACKGROUND",
    getTitle: () => "Background Check",
  },
  [CompanySection.BUYER_BEWARE]: {
    navLabel: "RECORD",
    getTitle: ({ deceptionCounts }) =>
      getBuyerBewareSectionTitle(deceptionCounts),
  },
}
